import React, { useState } from "react";
import axios from "axios";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { getServerUrlCandidates } from "../../utils/serverUrl";

const TIMEZONE_OPTIONS = [
  "Eastern Standard Time",
  "Central Standard Time",
  "Mountain Standard Time",
  "Pacific Standard Time",
  "UTC",
];

export default function CreateMonthlySyncGarageForm({
  companiesAndGarages,
  setSnackbarInfo,
  setOpenSimpleSnackbar,
  onSuccess,
}) {
  const [garageName, setGarageName] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [totalSpaces, setTotalSpaces] = useState("");
  const [driveFolderId, setDriveFolderId] = useState("");
  const [garageImage, setGarageImage] = useState("");
  const [sqlTimezone, setSqlTimezone] = useState("Eastern Standard Time");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const companyNames = Object.keys(companiesAndGarages || {});

  const handleGarageNameChange = (e) => {
    setGarageName(e.target.value);
  };

  const handleCompanyToggle = (company) => {
    setSelectedCompanies((prev) =>
      prev.includes(company)
        ? prev.filter((c) => c !== company)
        : [...prev, company]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!garageName.trim()) {
      setError("Garage name is required.");
      return;
    }
    if (selectedCompanies.length === 0) {
      setError("Select at least one company.");
      return;
    }
    if (!driveFolderId.trim()) {
      setError("Google Drive Folder ID is required.");
      return;
    }

    setIsSubmitting(true);

    const token = sessionStorage.getItem("token");
    const urls = getServerUrlCandidates("/admin/create-monthly-sync-garage");

    let lastError = null;
    for (const url of urls) {
      try {
        await axios.post(
          url,
          {
            garageName: garageName.trim(),
            companyIds: selectedCompanies,
            totalSpaces: Number(totalSpaces) || 0,
            driveFolderId: driveFolderId.trim(),
            garageImage: garageImage.trim(),
            sqlTimezone,
          },
          {
            headers: { authorization: `Bearer ${token}` },
          }
        );

        setSnackbarInfo({
          text: `Monthly-sync garage "${garageName}" created successfully!`,
          severity: "success",
        });
        setOpenSimpleSnackbar(true);

        setGarageName("");
        setSelectedCompanies([]);
        setTotalSpaces("");
        setDriveFolderId("");
        setGarageImage("");
        setSqlTimezone("Eastern Standard Time");

        if (onSuccess) onSuccess();
        setIsSubmitting(false);
        return;
      } catch (err) {
        lastError = err;
      }
    }

    const message =
      lastError?.response?.data?.error ||
      lastError?.message ||
      "Failed to create garage.";
    setError(message);
    setIsSubmitting(false);
  };

  return (
    <div className="p-3" style={{ maxWidth: 600 }}>
      <h5>Create Monthly-Sync Garage</h5>
      <p className="text-muted" style={{ fontSize: 14 }}>
        Sets up the Firestore config for a new monthly-sync garage. All garages
        share the same database. The sync job picks up the new garage automatically.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Garage Name *</Form.Label>
          <Form.Control
            type="text"
            value={garageName}
            onChange={handleGarageNameChange}
            placeholder="e.g. newgarage"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Company(ies) *</Form.Label>
          {companyNames.map((company) => (
            <Form.Check
              key={company}
              type="checkbox"
              label={company}
              checked={selectedCompanies.includes(company)}
              onChange={() => handleCompanyToggle(company)}
            />
          ))}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Total Parking Spaces</Form.Label>
          <Form.Control
            type="number"
            value={totalSpaces}
            onChange={(e) => setTotalSpaces(e.target.value)}
            placeholder="e.g. 57"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Google Drive Folder ID *</Form.Label>
          <Form.Control
            type="text"
            value={driveFolderId}
            onChange={(e) => setDriveFolderId(e.target.value)}
            placeholder="Folder ID from Google Drive URL"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Garage Image URL</Form.Label>
          <Form.Control
            type="text"
            value={garageImage}
            onChange={(e) => setGarageImage(e.target.value)}
            placeholder="https://..."
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>SQL Timezone</Form.Label>
          <Form.Select
            value={sqlTimezone}
            onChange={(e) => setSqlTimezone(e.target.value)}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            "Create Garage"
          )}
        </Button>
      </Form>
    </div>
  );
}
